import { useEffect, useState } from 'react'

/** Reactive media-query hook — returns true when the query matches. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    const mql = window.matchMedia(query)
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches)
    mql.addEventListener('change', handler)
    // Sync in case the initial value drifted between SSR and client
    setMatches(mql.matches)
    return () => mql.removeEventListener('change', handler)
  }, [query])

  return matches
}

/** Kanonische Phone-Definition — EINZIGE Quelle für den Query-String, nicht
 * erneut hardcoden. Zwei ODER-Zweige:
 *   1. `(max-width: 639px)` — Portrait-Phones (< Tailwind `sm`), wie bisher.
 *   2. `((pointer: coarse) and (max-height: 500px))` — Phones im QUERFORMAT
 *      (z.B. 844×390): Touch-Gerät + niedrige Höhe. iPad mini quer ist
 *      744px hoch, Desktops haben pointer:fine → beide fallen NICHT hinein.
 * Die Tailwind-Variante `phone:` (tailwind.config.js, raw-Screen) MUSS
 * byte-identisch bleiben — Drift-Guard in use-media-query.test.ts. */
export const PHONE_MEDIA_QUERY = '(max-width: 639px), ((pointer: coarse) and (max-height: 500px))'

/** true auf Phones (hoch UND quer) — Landscape-Phones bekommen damit die
 * komplette Mobile-Shell (Top-/Bottom-Bar, alle isMobile-Zweige). */
export function useIsMobile(): boolean {
  return useMediaQuery(PHONE_MEDIA_QUERY)
}

/** Tablet-Band 640–1023 (T2): Icon-Rail links + MobileShellBottom unten,
 * Seiten behalten ihre Desktop-Köpfe. Bewusst REIN breitenbasiert (kein
 * pointer-Gate) — auch schmale Desktop-Fenster bekommen Rail + Bar.
 * ACHTUNG: Landscape-Phones (844×390) matchen dieses Band AUCH — die
 * Phone-Definition (useIsMobile) gewinnt dort per `!isPhone`-Guard beim
 * Konsumenten (__root Rail) bzw. per ODER-Verknüpfung (Bottom-Bar). */
export const TABLET_MEDIA_QUERY = '(min-width: 640px) and (max-width: 1023px)'

/** true im Tablet-Band 640–1023 (breitenbasiert, s. TABLET_MEDIA_QUERY). */
export function useIsTabletBand(): boolean {
  return useMediaQuery(TABLET_MEDIA_QUERY)
}

/** Kanonische „Shell-Bars sind sichtbar"-Query (T3): Phone-Shell (hoch +
 * quer) ODER Tablet-Band 640–1023 — als Kommaliste (= ODER) aus den beiden
 * Quell-Konstanten komponiert, damit nichts eigenständig driften kann
 * (Guard in use-media-query.test.ts). Entspricht `showShellBars ||
 * showTabletBar` in __root (ohne dessen loggedIn-Gate). */
export const SHELL_BARS_MEDIA_QUERY = `${PHONE_MEDIA_QUERY}, ${TABLET_MEDIA_QUERY}`

/** true, wenn die Bottom-Bar der Shell existiert (Phone ODER Tablet-Band) —
 * DIE Registrierungs-Bedingung für Seiten-Bottom-Slots (MobileBottomSlots).
 * NICHT für Top-Center-Inhalte verwenden: die Top-Leiste gibt es nur am
 * Phone (MobileTopCenter bleibt intern isPhone-gated). */
export function useShellBars(): boolean {
  return useMediaQuery(SHELL_BARS_MEDIA_QUERY)
}
